import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import { fetchOverview } from '@/api/overview';
import { useAccountStore } from '@/stores/accountStore';
import { useBriefingStore } from '@/stores/briefingStore';
import { UNIFIED_ACCOUNT_ID } from '@/constants/mailbox';

const REFRESH_TTL_MS = 5 * 60 * 1000;

/**
 * Counts shown as badges on the mail sub-view tabs — currently just surveys
 * flagged for review. Refreshed lazily with a TTL by whichever tab strip
 * mounts first; the Surveys page pushes its exact count from its own load so
 * the badge settles without waiting for TTL.
 */
interface MailViewBadgesState {
  surveysToReview: number;
  lastFetchedAt: number;
  isFetching: boolean;
  refresh: (options?: { force?: boolean }) => Promise<void>;
  setSurveysToReview: (count: number) => void;
}

export const useMailViewBadgesStore = create<MailViewBadgesState>()(
  conditionalDevtools(
    (set, get) => ({
      surveysToReview: 0,
      lastFetchedAt: 0,
      isFetching: false,

      setSurveysToReview: (count) => set({ surveysToReview: count }),

      refresh: async (options = {}) => {
        const { force = false } = options;
        const state = get();
        if (state.isFetching) {
          return;
        }
        if (!force && Date.now() - state.lastFetchedAt < REFRESH_TTL_MS) {
          return;
        }
        const accounts = useAccountStore
          .getState()
          .accounts.filter((account) => account.id !== UNIFIED_ACCOUNT_ID);
        if (accounts.length === 0) {
          // Accounts not loaded yet — don't stamp the TTL on an empty read.
          return;
        }
        // Prime the briefing tab badge once; after the first load, realtime
        // briefing-updated events and tab visits keep entries fresh.
        if (useBriefingStore.getState().entries === null) {
          void useBriefingStore.getState().fetchForAccount(UNIFIED_ACCOUNT_ID);
        }
        set({ isFetching: true });
        try {
          const surveyResults = await Promise.all(
            accounts.map((account) =>
              fetchOverview(account.id, {
                limit: 0,
                includeSurveys: true,
                unreadOnly: false,
                inboxOnly: true,
              }).catch(() => null)
            )
          );
          const surveysToReview = surveyResults.reduce(
            (sum, result) => sum + (result?.surveys?.flagged_count ?? 0),
            0
          );
          set({
            surveysToReview,
            lastFetchedAt: Date.now(),
          });
        } finally {
          set({ isFetching: false });
        }
      },
    }),
    { name: 'MailViewBadgesStore' }
  )
);
